import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Navigation, Phone, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PublicOrganization } from '../types';

// Address, online info, phone and email. Every row shows only when that data
// exists (and the business allows it), so the card can shrink to one line.
export function hasContact(organization: PublicOrganization): boolean {
  const page = organization.booking_page;
  return !!(page.location || page.phone || page.email || (page.show_online && !page.location));
}

export function ContactCard({ organization, elevated = false }: { organization: PublicOrganization; elevated?: boolean }) {
  const { t } = useTranslation('bookingPage');
  const page = organization.booking_page;
  if (!hasContact(organization)) return null;
  const onlineOnly = page.show_online && !page.location;

  return (
    <section className={cn('flex flex-col gap-3.5 rounded-4xl border border-line bg-surface p-5 sm:p-[22px]', elevated && 'shadow-card')} data-testid="contact-card">
      <h3 className="inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-ink">
        <Phone className="h-4 w-4 text-soft" />
        {t('contact.title')}
      </h3>

      {page.location && (
        <div className="flex flex-col gap-1.5" data-testid="contact-address">
          <span className="inline-flex items-start gap-2 text-body font-semibold text-ink">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-soft" />
            {page.location.address}
          </span>
          {page.location.directions_url && (
            <a href={page.location.directions_url} target="_blank" rel="noreferrer" className="inline-flex min-h-[36px] items-center gap-1.5 pl-6 text-body font-medium text-brand-500 hover:text-brand-700">
              <Navigation className="h-3.5 w-3.5" />
              {t('location.directions')}
            </a>
          )}
        </div>
      )}

      {onlineOnly && (
        <div className="flex items-start gap-3" data-testid="contact-online">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50">
            <Video className="h-[18px] w-[18px] text-brand-500" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-body font-semibold text-ink">{t('online.title')}</span>
            <span className="text-body-sm text-soft">{t('online.body')}</span>
          </div>
        </div>
      )}

      {(page.phone || page.email) && (
        <>
          {(page.location || onlineOnly) && <div className="h-px bg-line" />}
          <div className="flex flex-col gap-1 text-body text-soft">
            {page.phone && <span data-testid="contact-phone">{page.phone}</span>}
            {page.email && <span className="break-all" data-testid="contact-email">{page.email}</span>}
          </div>
          <div className={cn('grid gap-2', page.phone && page.email ? 'grid-cols-2' : 'grid-cols-1')}>
            {page.phone && (
              <Button asChild variant="outline" className="h-11">
                <a href={`tel:${page.phone.replace(/\s+/g, '')}`}><Phone className="h-4 w-4" />{t('contact.call')}</a>
              </Button>
            )}
            {page.email && (
              <Button asChild variant="outline" className="h-11">
                <a href={`mailto:${page.email}`}><Mail className="h-4 w-4" />{t('contact.email')}</a>
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
